
import {defineComponent, reactive, ref} from 'vue';
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import SystemImport from "@/apps/administration/components/system/SystemImport.vue";
import {UploadFile} from "@/shims-prime-vue";
import {ToastManager} from "@/util/ToastManager";
import {Clients, ConsoleLogger, Exporter, LocatorConfig, SqueezeConfig} from "@dex/squeeze-configframework";
import {DocumentClassConfig, FieldsConfig, TableConfig} from "@dex/squeeze-configframework/build/export/SqueezeConfig";
import ConfigurationsDataList from "@/apps/administration/components/system/configurations/ConfigurationsDataList.vue";
import {
	DocumentClassDto,
	DocumentField,
	DocumentLocator,
	DocumentTableColumn,
	MasterDataTable
} from "@dex/squeeze-client-ts";
import {MasterDataConfig} from "@dex/squeeze-configframework/build/export/MasterDataConfig";
import {ClientManager} from "@/singletons/ClientManager";
import {downloadData} from "@/util/Export";
import ProgressBar from "primevue/progressbar";
import EntryDialog from "@/components/EntryDialog.vue";
import Message from "primevue/message";

export interface TreeTableEntry {
	key: string;
	data: {
		label: string | undefined;
		type?: string | undefined;
	};
	children?: TreeTableEntry[];
}

export default defineComponent({
	name: "ConfigurationsView",
	components: {
		Message,
		EntryDialog,
		ProgressBar,
		ConfigurationsDataList,
		TabView,
		TabPanel,
		SystemImport,
	},
	setup() {
		const {t} = useI18n();
		const toast = useToast();

		/** Show Loading on load data */
		const loading = ref<boolean>(false);

		/** Current active tab option */
		const activeTabOption = ref(0);

		/** All tab options of a file */
		const tabOptions = ref([
			{icon: 'mdi mdi-file-import-outline', value: t('Squeeze.General.Import')},
			{icon: 'mdi mdi-file-export-outline', value: t('Squeeze.General.Export')},
		]);

		/** List of all files */
		const files = ref<UploadFile[]>([]);

		/** Used File-Type for Upload */
		const fileType = ref('json');

		/** Triggered when (all) field values are invalid */
		const showErrorMessage = ref<boolean>(false);

		/** All file data */
		const fileData = reactive<SqueezeConfig>({
			documentClasses: [],
			locators: [],
			masterDataTables: [],
		});

		/** Should the data list be shown? */
		const showDataList = ref<boolean>(false);

		/** Data list of imported file */
		const dataList = ref<TreeTableEntry[]>([]);

		/** Is the import of the document classes first (before locator import)? */
		const importDocumentClassesFirst = ref<boolean>(false);

		/** Is the Progress Bar visible? */
		const showProgressBar = ref<boolean>(false);

		/**
		 * Triggered on update of file upload
		 * @param data
		 * @param isInvalid
		 */
		const onUpdate = (data: any, isInvalid: boolean) => {
			showErrorMessage.value = isInvalid;
			fileType.value = data.fileType;
		}

		/**
		 * Process the document class field data
		 * the used master data table (with all columns) and the used locator
		 * @param {DocumentField} field
		 */
		const processDcFieldData = (field: DocumentField) => {
			const fieldChildren: TreeTableEntry[] = [];

			if (field.lookup) {
				// find used master data table in field lookup
				if (field.lookup.tableId && fileData.masterDataTables) {
					fileData.masterDataTables.forEach((table: MasterDataConfig, index: number) => {
						if (table.masterDataTable.id === field.lookup?.tableId) {
							fieldChildren.push({key: "2-" + index, data: {label: table.masterDataTable.name, type: t('Squeeze.MasterData.MasterDataTable')}, children: []});
						}
					})
				}

				// find used locator in field data
				if (field.locatorId && fileData.locators) {
					fileData.locators.forEach((locator: LocatorConfig, index: number) => {
						if (locator.locator.id === field.locatorId && locator.locator.name) {
							fieldChildren.push({key: "1-" + index, data: {label: locator.locator.name, type: t('Squeeze.DocumentClasses.Locator')}, children: []});
						}
					})
				}
			}

			return fieldChildren;
		}

		/**
		 * Process the document class table column data
		 * the used locator(s)
		 * @param {DocumentTableColumn} column
		 */
		const processDcTableColumnData = (column: DocumentTableColumn) => {
			const columnChildren: TreeTableEntry[] = [];

			// find used header and value locator
			if (column.headerLocatorId || column.valueLocatorId) {
				fileData.locators.forEach((locator: LocatorConfig, index: number) => {
					if (locator.locator.id === column.headerLocatorId) {
						columnChildren.push({key: "1-" + index, data: {label: locator.locator.name, type: t('Squeeze.DocumentClasses.HeaderLocator')}});
					} else if (locator.locator.id === column.valueLocatorId) {
						columnChildren.push({key: "1-" + index, data: {label: locator.locator.name, type: t('Squeeze.DocumentClasses.ValueLocator')}});
					}
				});
			}

			return columnChildren;
		}

		/**
		 * Process the document class table data
		 * the used locator
		 * @param {TableConfig} table
		 * @param {number} dcIndex
		 * @param {number} tableIndex
		 */
		const processDcTable = (table: TableConfig, dcIndex: number, tableIndex: number) => {
			let tableChildren: TreeTableEntry[] = [];

			if (table.columns.length > 0) {
				tableChildren = table.columns.map((column: DocumentTableColumn, columnIndex: number) => {
					return {key: '0-' + dcIndex + '-table-' + tableIndex + '-' + columnIndex, data: {label: column.name, type: t('Squeeze.General.TableColumn')}, children: processDcTableColumnData(column)};
				})
			}

			if (table.table.locatorId) {
				fileData.locators.forEach((locator: LocatorConfig, index: number) => {
					if (locator.locator.id === table.table.locatorId) {
						tableChildren.push({key: '1-' + index, data: {label: locator.locator.name, type: t('Squeeze.DocumentClasses.Locator')}})
					}
				})
			}

			return tableChildren;
		}

		/**
		 * Process the config data of document class
		 * join two arrays
		 * @param dc current document class
		 * @param index document class index
		 */
		const processConfigDcData = (dc: DocumentClassConfig, index: number) => {
			// map field groups data
			const fieldGroups = dc.fieldGroups.map((fieldGroup: FieldsConfig, fieldGroupIndex: number) => {
				return {key: '0-' + index + '-fieldgroup-' + fieldGroupIndex, data: {label: fieldGroup.fieldGroup.name, type: t('Squeeze.DocumentClasses.FieldGroup')}, children: fieldGroup.fields.map((field: DocumentField, fieldIndex: number) => {
					return {key: '0-' + index + '-fieldgroup-' + fieldGroupIndex + '-' + fieldIndex, data: {label: field.name, type: t('Squeeze.DocumentClasses.Field')}, children: processDcFieldData(field)}})}
			});

			// map tables data
			const tables = dc.tables.map((table: TableConfig, tableIndex: number) => {
				return {key: '0-' + index + '-table-' + tableIndex, data: {label: table.table.name, type: t('Squeeze.DocumentClasses.Table')}, children: processDcTable(table, index, tableIndex)}
			});

			// Document class data
			const dcData: TreeTableEntry[] = [];

			return dcData.concat(fieldGroups, tables);
		}

		/**
		 * Process the config data of locator details
		 * @param {LocatorConfig} locator
		 * @param {string} locatorIndex
		 */
		const processLocatorDetailsData = (locator: LocatorConfig, locatorIndex?: number) => {
			const children: TreeTableEntry[] = [];

			// get used locator details of locator
			const locatorDetails = locator.locatorDetails;
			if (locatorDetails) {
				// find used source locator of locator
				if (locator.locatorDetails.sourceLocator && locator.locatorDetails.sourceLocator.name && locator.locator.sourceLocatorId) {
					const currentLocator: DocumentLocator = locator.locatorDetails.sourceLocator;
					const locatorIndex = fileData.locators ? fileData.locators.findIndex(loc => loc.locator.name === currentLocator.name) : Math.random();
					children.push({key: '1-' + locatorIndex, data: {label: currentLocator.name, type: t('Squeeze.Locators.Tabs.SourceLocator')}});
				}

				if (locatorDetails.sourceTable) {
					// master data table of tableKeyColumnFilter
					if (locatorDetails.sourceTable.masterDataTables.length > 0) {
						const currentMasterData: MasterDataTable = locatorDetails.sourceTable.masterDataTables[0];
						const masterDataIndex = fileData.masterDataTables ? fileData.masterDataTables.findIndex(table => table.masterDataTable.name === currentMasterData.name) : Math.random();
						children.push({key: '2-' + masterDataIndex, data: {label: currentMasterData.name, type: t('Squeeze.MasterData.MasterDataTable')}});
					}

					// document class of tableKeyFieldFilter
					if (locatorDetails.sourceTable.documentClass && locatorDetails.sourceTable.documentClass.documentClass && locatorDetails.sourceTable.documentClass.documentClass.name) {
						const currentDocumentClass: DocumentClassDto = locatorDetails.sourceTable.documentClass.documentClass;
						const dcIndex = fileData.documentClasses ? fileData.documentClasses.findIndex(dc => dc.documentClass.name === locatorDetails.sourceTable!.documentClass.documentClass.name) : Math.random();
						children.push({key: '0-' + dcIndex, data: {label: currentDocumentClass.name, type: t('Squeeze.DocumentClasses.DocumentClass')}, children: processConfigDcData(locatorDetails.sourceTable.documentClass, Math.random())});
					}

					// locator of tableKeyLocatorFilter
					if (locatorDetails.sourceTable.locators.length > 0) {
						locatorDetails.sourceTable.locators.map((locatorConfig: LocatorConfig) => {
							let locIndex = fileData.locators ? fileData.locators.findIndex(loc => loc.locator.name === locatorConfig.locator.name) : Math.random();
							if (locIndex === -1) locIndex = Math.random();
							children.push({key: locatorIndex ? '1-' + locatorIndex + '-' + locIndex : '1-'+ locIndex, data: {label: locatorConfig.locator.name, type: t('Squeeze.DocumentClasses.Locator')}, children: processLocatorDetailsData(locatorConfig, locIndex)});
						})
					}
				} else if (locatorDetails.valueFromRegexDetails && locatorDetails.valueFromRegexDetails.masterDataTables.length > 0) {
					locatorDetails.valueFromRegexDetails.masterDataTables.map((table: MasterDataTable) => {
						const tableIndex = fileData.masterDataTables ? fileData.masterDataTables.findIndex(t => t.masterDataTable.name === table.name) : Math.random();
						children.push({key: '2-'+ tableIndex, data: {label: table.name, type: t('Squeeze.MasterData.MasterDataTable')}});
					})
				} else if (locatorDetails.invoiceAmountDetails && locatorDetails.invoiceAmountDetails.locators.length > 0) {
					locatorDetails.invoiceAmountDetails.locators.map((locatorConfig: LocatorConfig) => {
						let locIndex = fileData.locators ? fileData.locators.findIndex(l => l.locator.name === locatorConfig.locator.name) : Math.random();
						if (locIndex === -1) locIndex = Math.random();
						children.push({key: locatorIndex ? '1-' + locatorIndex + '-' + locIndex : '1-'+ locIndex, data: {label: locatorConfig.locator.name, type: t('Squeeze.DocumentClasses.Locator')}, children: processLocatorDetailsData(locatorConfig, locIndex)});
					})
				} else if (locatorDetails.dbLinkLocatorDetails && locatorDetails.dbLinkLocatorDetails.locators.length > 0) {
					locatorDetails.dbLinkLocatorDetails.locators.map((locatorConfig: LocatorConfig) => {
						let locIndex = fileData.locators ? fileData.locators.findIndex(loc => loc.locator.name === locatorConfig.locator.name) : Math.random();
						if (locIndex === -1) locIndex = Math.random();
						children.push({key: locatorIndex ? '1-' + locatorIndex + '-' + locIndex : '1-'+ locIndex, data: {label: locatorConfig.locator.name, type: t('Squeeze.DocumentClasses.Locator')}, children: processLocatorDetailsData(locatorConfig, locIndex)});
					})
				} else if (locatorDetails.lineItemsDetails && locatorDetails.lineItemsDetails.lineItemsDetails.tableId) {
					const dc = locatorDetails.lineItemsDetails.documentClass;
					children.push({key: '0-0', data: {label: dc.documentClass.name, type: t('Squeeze.DocumentClasses.DocumentClass')}, children: processConfigDcData(dc, Math.random())});
				}
			}
			return children;
		}

		/** Process to build the data config */
		const processConfigData = () => {
			showDataList.value = true;

			if (fileData.documentClasses) {
				dataList.value.push({
					key: '0',
					data: {
						label: t('Squeeze.DocumentClasses.DocumentClasses'),
					},
					children: fileData.documentClasses.map((dc: DocumentClassConfig, index: number) => {
						return {key: '0-' + index, data: {label: dc.documentClass.name, type: t('Squeeze.DocumentClasses.DocumentClass')}, children: processConfigDcData(dc, index)}
					}),
				});
			}

			if (fileData.locators) {
				dataList.value.push({
					key: '1',
					data: {
						label: t('Squeeze.Breadcrumbs.Locators'),
					},
					children: fileData.locators.map((locator: LocatorConfig, index: number) => {
						return {key: '1-' + index, data: {label: locator.locator.name, type: t('Squeeze.DocumentClasses.Locator')}, children: processLocatorDetailsData(locator)}
					}),
				});
			}

			if (fileData.masterDataTables) {
				dataList.value.push({
					key: '2',
					data: {
						label: t('Squeeze.Breadcrumbs.MasterDataList'),
					},
					children: fileData.masterDataTables.map((masterDataTable: MasterDataConfig, index: number) => {
						return {key: '2-' + index, data: {label: masterDataTable.masterDataTable.name, type: t('Squeeze.MasterData.MasterDataTable')}, children: []}
						// columns are not shown - fix for later --> children: masterDataTable.columns?.map((column, columnIndex) => return {key: '2-' + index + '-' + columnIndex, data: {label: column.name, type: t('Squeeze.General.TableColumn')}}})}
					}),
				});
			}
		}

		/** Export the system backup before the import can start */
		const exportBackup = async () => {
			const clients: Clients = {
				documentClass: ClientManager.getInstance().squeeze.documentClass,
				masterData: ClientManager.getInstance().squeeze.masterData,
				locator: ClientManager.getInstance().squeeze.locator,
			}

			const exporter = new Exporter(clients, new ConsoleLogger());

			const docClassData: SqueezeConfig = await exporter.run();
			downloadData('backup_' + new Date().toJSON().slice(0, 10), JSON.stringify(docClassData,null, 2));
		}

		/**
		 * Starts the File upload for the given files
		 * In this function a (import) file will be read
		 */
		const startFileUpload = () => {
			loading.value = true;

			files.value
				.forEach((file: any, index: number) => {
					const idx = index;
					files.value[idx].error = false;
					files.value[idx].errorText = "";
					files.value[idx].loading = true;
					files.value = [...files.value];

					files.value[idx].text()
						.then(async (data: string) => {
							files.value[idx].uploadFinished = true;
							dataList.value = [];

							const allData: SqueezeConfig = JSON.parse(data);
							fileData.documentClasses = allData.documentClasses;
							fileData.locators = allData.locators;
							fileData.masterDataTables = allData.masterDataTables;

							// when document classes in import file not defined and locators is defined, then set value to true
							if (!fileData.documentClasses && fileData.locators) {
								importDocumentClassesFirst.value = true;
							}
							showProgressBar.value = true;

							await exportBackup();
							processConfigData();
						})
						.catch(err => {
							files.value[idx].error = true;
							files.value[idx].errorText = err.message;
							ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
						})
						.finally(() => {
							files.value[idx].loading = false;
							files.value = [...files.value];

							showProgressBar.value = false;
							loading.value = false;
						});
				})
		}

		/** Uploads the files from the file-uploader
		 * @param filesSend
		 */
		const fileUploader = (filesSend: UploadFile[]) => {
			files.value = filesSend;
			startFileUpload();
		}

		/** Close the DataList-Dialog */
		const onCloseDialog = () => {
			dataList.value = [];
			showDataList.value = false;
		}


		/** Remove (all) files after import */
		const removeFiles = () => {
			files.value = [];
		}

		return {
			t,
			toast,
			loading,
			activeTabOption,
			tabOptions,
			files,
			fileType,
			showErrorMessage,
			fileData,
			showDataList,
			dataList,
			importDocumentClassesFirst,
			showProgressBar,
			onUpdate,
			fileUploader,
			startFileUpload,
			onCloseDialog,
			removeFiles,
		};
	},
});

